import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { FaWhatsapp, FaInstagram, FaEnvelope } from 'react-icons/fa'
import Card from '../components/Card'
import LoadingSpinner from '../components/LoadingSpinner'
import api from '../services/api'
import { getToken, clearAuth, saveUserData } from '../utils/userData'

const Facilitators = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const meetupId = searchParams.get('meetupId') || localStorage.getItem('currentMeetupId')
  const [facilitators, setFacilitators] = useState([])
  const [loading, setLoading] = useState(true)
  const [authChecking, setAuthChecking] = useState(true)
  const [error, setError] = useState(null)
  const [selectedTag, setSelectedTag] = useState('all')

  useEffect(() => {
    // Check authentication and facilitator status
    const checkAuthAndLoadData = async () => {
      const token = getToken()
      if (!token) {
        navigate('/')
        return
      }

      try {
        // Verify user is a facilitator
        const response = await api.get('/api/auth/me')
        if (response.data.success) {
          const userData = response.data.data
          saveUserData(userData)
          
          if (!userData.isFacilitator) {
            // Not a facilitator - redirect to dashboard
            navigate('/dashboard')
            return
          }
        }
        setAuthChecking(false)
        loadFacilitators()
      } catch (err) {
        console.error('Error checking auth:', err)
        if (err.response?.status === 401) {
          clearAuth()
          navigate('/')
        } else {
          setError('Failed to verify authorization. Please try again.')
          setAuthChecking(false)
          setLoading(false)
        }
      }
    }

    checkAuthAndLoadData()
  }, [navigate])

  const loadFacilitators = async () => {
    try {
      setLoading(true)
      const response = await api.get('/api/facilitators')
      setFacilitators(response.data.data)
      setLoading(false)
    } catch (err) {
      console.error('Error loading facilitators:', err)
      if (err.response?.status === 401) {
        clearAuth()
        navigate('/')
      } else {
        setError('Failed to load facilitators. Please try again.')
        setLoading(false)
      }
    }
  }

  // Get all unique tags from facilitators
  const allTags = ['all', ...new Set(facilitators.flatMap((f) => f.tags.map((t) => t.name)))]

  // Helper function to format phone number for WhatsApp
  const formatPhoneForWhatsApp = (phone) => {
    // Remove all non-digit characters except +
    const cleaned = phone.replace(/[^\d+]/g, '')
    // If it doesn't start with +, assume it's a local number and add country code
    // For now, we'll just return the cleaned number
    return cleaned
  }

  // Helper function to format Instagram username
  const formatInstagramUsername = (instagramId) => {
    // Remove @ if present and any whitespace
    return instagramId.replace(/^@/, '').trim()
  }

  // Helper function to format website URL
  const formatWebsiteUrl = (url) => {
    // If the URL already has a protocol, return it as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    // Otherwise, add https:// as the default protocol
    return `https://${url}`
  }

  const filteredFacilitators =
    selectedTag === 'all'
      ? facilitators
      : facilitators.filter((f) => f.tags.some((t) => t.name === selectedTag))

  if (loading || authChecking) {
    return (
      <div className="container mx-auto px-4 py-20 flex justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-20">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Error</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={loadFacilitators}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-cream min-h-screen py-8">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="mb-8">
            {meetupId && (
              <button
                onClick={() => navigate(`/meetup/${meetupId}`)}
                className="text-primary-600 hover:text-primary-700 mb-4 flex items-center gap-2 transition-colors"
              >
                <span>←</span> Back to Meetup
              </button>
            )}
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Facilitator <span className="text-primary-600">Directory</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                View all wellness facilitators in the platform
              </p>
            </div>
          </div>

          {/* Filter Chips */}
          {allTags.length > 1 && (
            <div className="mb-8 flex flex-wrap gap-3 justify-center">
              {allTags.map((tag, index) => (
                <motion.button
                  key={tag}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => setSelectedTag(tag)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedTag === tag
                      ? 'bg-primary-600 text-white shadow-md'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {tag === 'all' ? 'All Tags' : tag}
                </motion.button>
              ))}
            </div>
          )}

          {/* Facilitators Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredFacilitators.map((facilitator, index) => (
              <motion.div
                key={facilitator.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-xl transition-all duration-300 flex flex-col">
                  {/* Avatar and Name */}
                  <div className="text-center mb-4">
                    <Link 
                      to={facilitator.slug ? `/facilitator/${facilitator.slug}` : '#'}
                      className="block"
                    >
                      <div className="text-6xl mb-3 hover:scale-105 transition-transform">
                        {facilitator.name
                          .split(' ')
                          .map((n) => n[0])
                          .join('')
                          .toUpperCase()
                          .slice(0, 2)}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1 hover:text-primary-600 transition-colors">
                        {facilitator.name}
                      </h3>
                    </Link>
                    {facilitator.email && (
                      <a
                        href={`mailto:${facilitator.email}`}
                        className="text-sm text-primary-600 hover:text-primary-700 hover:underline transition-colors flex items-center justify-center gap-2"
                      >
                        <FaEnvelope className="text-base" />
                        <span>{facilitator.email}</span>
                      </a>
                    )}
                  </div>

                  {/* Bio */}
                  {facilitator.bio && (
                    <p className="text-sm text-gray-600 text-center mb-4 line-clamp-3">
                      {facilitator.bio}
                    </p>
                  )}

                  {/* Tags */}
                  {facilitator.tags && facilitator.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4 justify-center">
                      {facilitator.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium"
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Contact Info */}
                  <div className="mt-auto space-y-2 text-sm text-gray-600">
                    {facilitator.phoneNumber && (
                      <p className="text-center">
                        <a
                          href={`https://wa.me/${formatPhoneForWhatsApp(facilitator.phoneNumber)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 text-primary-600 hover:text-primary-700 hover:underline transition-colors"
                        >
                          <FaWhatsapp className="text-lg" />
                          <span>{facilitator.phoneNumber}</span>
                        </a>
                      </p>
                    )}
                    {facilitator.instagramId && (
                      <p className="text-center">
                        <a
                          href={`https://instagram.com/${formatInstagramUsername(facilitator.instagramId)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 text-primary-600 hover:text-primary-700 hover:underline transition-colors"
                        >
                          <FaInstagram className="text-lg" />
                          <span>{facilitator.instagramId}</span>
                        </a>
                      </p>
                    )}
                    {facilitator.linkedInUrl && (
                      <p className="text-center truncate">
                        💼{' '}
                        <a
                          href={facilitator.linkedInUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline"
                        >
                          LinkedIn
                        </a>
                      </p>
                    )}
                    {facilitator.website && (
                      <p className="text-center truncate">
                        🌐{' '}
                        <a
                          href={formatWebsiteUrl(facilitator.website)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline"
                        >
                          Website
                        </a>
                      </p>
                    )}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Empty State */}
          {filteredFacilitators.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                No facilitators found
              </h3>
              <p className="text-gray-600 mb-6">
                Try selecting a different tag filter
              </p>
              <button
                onClick={() => setSelectedTag('all')}
                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                View All Facilitators
              </button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  )
}

export default Facilitators

